import React, { useEffect, useState } from 'react';
import { getUrlParameter } from '@services/url-services';
import ArticleList from './article-list';
import NewsroomNavigation from './newsroom-navigation/newsroom-navigation';
import { createBrowserHistory } from 'history';
import Loader from '@components/loader';
import Pagination from 'rc-pagination';
import { safeLanguageUrlPrefix } from '@services/drupal-services';
import Translate from '@components/translation/translate-text-wrapper';
import axios from 'axios';
import { safeCurrentLanguage } from '@services/drupal-services';

/**
 * French Canadian blog promotion data
 */
export const frCaBlog = {
  title:
    "Transformation de l'oncologie en Nouvelle-Écosse : optimisation des processus à l'aide de nouvelles technologies",
  date_release: 'Dec 14, 2023',
  node_alias:
    '/fr-ca/resources-support/blogs/oncology-transformation-nova-scotia-optimizing-processes',
  widen_image_id: 'zephhli2co',
  description: ``,
  summary:
    "Imaginez un programme régional complet de soins contre le cancer qui rassemble tous les centres, urbains et ruraux, toutes les disciplines d'oncologie, ainsi que l'éventail complet des soins contre le cancer, du diagnostic à la survie et au...",
  category_id: '168',
};

/**
 * Aricle Props.
 */
type ArticleProps = {
  body: string;
  date_release: string;
  news_type: string;
  nid: string;
  node_alias: string;
  summary: string;
  title: string;
};

type PageInfoProps = {
  active_item_range: number[];
  active_page: number;
  items_per_page: number;
  page_count: number;
  total_count: number;
};

type ArticleData = {
  articleList?: ArticleProps[];
  pageInfo: PageInfoProps;
};

/**
 * Newsroom
 */
const Newsroom: React.FC<{ category: string; index: number }> = ({
  category,
  index,
}) => {
  const history = createBrowserHistory();
  const [newsroomCategory, pageType] = category.split(' ');
  const [articleData, setArticleData] = useState<ArticleData | null>(null);
  const [blogType, setBlogType] = useState<string | null>(
    getUrlParameter(window.location.href, 'type') ?? null
  );
  const [pageNumber, setPageNumber] = useState<string | number>(
    getUrlParameter(window.location.href, 'n') !== ''
      ? getUrlParameter(window.location.href, 'n')
      : 1
  );

  /**
   * Blog Type Titles.
   */
  const blogTypeTitles = {
    centerline: 'Centerline',
    proton: 'SpotOn',
    vision: 'VISion',
  };

  /**
   * Newsrooms API Article Types.
   */
  const newsroomTypes = {
    'press-release': {
      api: `/${safeLanguageUrlPrefix()}rest-api/varian-news-data?_format=json&type=press`,
      title: `Press Releases`,
      link: `/${safeLanguageUrlPrefix()}about-varian/newsroom/press-releases`,
    },
    'news-items': {
      api: `/${safeLanguageUrlPrefix()}rest-api/varian-news-data?_format=json&type=news`,
      title: `News Items`,
      link: `/${safeLanguageUrlPrefix()}about-varian/newsroom/news-items`,
    },
    blogs: {
      api: `/${safeLanguageUrlPrefix()}rest-api/varian-blog-data?_format=json${
        blogType ? `&type=${blogType}` : ``
      }`,
      title: 'Blog Posts',
      link: `/${safeLanguageUrlPrefix()}resources-support/blogs`,
    },
  };

  const { api, link, title } = newsroomTypes[newsroomCategory];

  /**
   * Get Articles.
   */
  const getArticles = (): void => {
    const limit = pageType ? 3 : 10;
    if (api.length > 0) {
      axios
        .get(`${api}&limit=${limit}&n=${pageNumber}`)
        .then((response) => {
          const { data } = response;
          if (data) {
            const { blogs, news, page_info: pageInfo } = data;
            if (blogs?.length > 0) {
              setArticleData({
                articleList: blogs,
                pageInfo: pageInfo,
              });
            } else if (news?.length > 0) {
              setArticleData({
                articleList: news,
                pageInfo: pageInfo,
              });
            }
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  /**
   * Pagination click.
   *
   * @param {number} pageNumber
   */
  const paginationClick = (pageNumber: number): void => {
    history.push(`${window.location.pathname}?n=${pageNumber}`, {
      some: 'state',
    });
    const scrollSelector: HTMLElement | null =
      document.querySelector('.newsroom-nav');
    scrollSelector?.scrollIntoView({ behavior: 'smooth' });
    setPageNumber(pageNumber);
    setArticleData({ articleList: [], pageInfo: {} });
  };

  /**
   * Fetch Articles.
   */
  useEffect(() => {
    getArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, blogType]);

  /**
   * Filter blogs.
   *
   * @param {string} category
   */
  const filterBlogs = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    category: string
  ): void => {
    event.preventDefault();
    const urlTypeParameter = category ? `?type=${category}` : ``;
    if (category) {
      history.push(`${window.location.pathname}${urlTypeParameter}`, {
        some: 'state',
      });
    }
    setArticleData({ articleList: [], pageInfo: {} });
    setPageNumber(1);
    setBlogType(category);
  };

  /**
   * Render
   */
  if (articleData) {
    const { articleList, pageInfo } = articleData;

    if (pageInfo) {
      const { active_page: activePage, total_count: totalCount } = pageInfo;

      /**
       * Promote French Canadian blog to top
       */
      if (
        activePage === 1 &&
        newsroomCategory === 'blogs' &&
        safeCurrentLanguage() === 'fr-ca'
      ) {
        articleList?.unshift(frCaBlog);
      }

      return (
        <>
          {/* Newsroom navigation */}
          {index === 0 && (
            <NewsroomNavigation
              pageType={pageType}
              blogType={blogType}
              newsroomCategory={newsroomCategory}
              filterBlogs={filterBlogs}
            />
          )}

          {/* Press releases, news items and blogs */}
          <div className="newsroom-articles large-vertical-margin-bottom medium-vertical-margin-top">
            {newsroomCategory === 'blogs' && blogType ? (
              <>
                <h2 className="blogs-heading">
                  <Translate>{title}</Translate>
                </h2>
                <h3 className="blog-type">
                  <Translate>{blogTypeTitles[blogType]}</Translate>
                </h3>
              </>
            ) : (
              <h2>
                <Translate>{title}</Translate>
              </h2>
            )}

            {articleList?.length > 0 ? (
              <ArticleList
                articleList={articleList}
                activePage={activePage}
                pageType={pageType}
                newsroomCategory={newsroomCategory}
              />
            ) : (
              <Loader />
            )}

            {pageType === 'homepage' && (
              <a className="featured-link newsroom-see-all" href={link}>
                <Translate>See all</Translate> <Translate>{title}</Translate>
              </a>
            )}

            {/* Pagination */}
            {pageType !== 'homepage' && totalCount > 10 && (
              <Pagination
                pageSize={10}
                total={totalCount}
                current={activePage}
                onChange={paginationClick}
                hideOnSinglePage={true}
                showPrevNextJumpers={false}
              />
            )}
          </div>
        </>
      );
    }
  }
  return null;
};

export default Newsroom;
